import React, { useEffect, ComponentProps, FC, ReactNode } from 'react';

import PropTypes, { Validator } from 'prop-types';

import { noop } from '@adsk/alloy-react-helpers';
import {
  LocaleProvider,
  supportedLocales,
  useLocale,
} from '@adsk/alloy-react-locale';
import { NotificationsProvider } from '@adsk/alloy-react-notification';
import { Reset } from '@adsk/alloy-react-theme';

import { AccountEntitlementsProvider } from './AccountEntitlementsContext';
import { AlloyLaunchDarklyProvider } from './AlloyLaunchDarklyProvider';
import AlloyLaunchDarklySubscriber from './AlloyLaunchDarklySubscriber';
import { CompaniesProvider } from './CompaniesContext';
import {
  AUTO_FETCH_TYPES,
  AutoFetchType,
  PRODUCT_IDS,
  TOOL_IDS,
} from './consts';
import { CurrentAccountIdProvider } from './CurrentAccountIdContext';
import { CurrentProductIdProvider } from './CurrentProductIdContext';
import { CurrentProjectIdProvider } from './CurrentProjectIdContext';
import { CurrentToolIdProvider } from './CurrentToolIdContext';
import { DeletedUsersProvider } from './DeletedUsers';
import {
  ProjectEntitlementsProvider,
  useProjectEntitlements,
  useUser,
} from './ProjectEntitlementsContext';
import { RolesProvider } from './RolesContext';
import { SubscriptionsProvider } from './SubscriptionsContext';
import { UserProvider } from './UserContext';
import { UsersProvider } from './UsersContext';

type RedirectError = {
  redirectUrl: string;
};

const getRedirectUrl = (error: unknown) => {
  if (
    error &&
    (error as Record<string, unknown>).hasOwnProperty('redirectUrl')
  ) {
    return (error as RedirectError).redirectUrl;
  }
  return undefined;
};

const CheckRedirectEntitlementsResponse = () => {
  const { error } = useProjectEntitlements();
  const redirectUrl = getRedirectUrl(error);
  useEffect(() => {
    if (redirectUrl) {
      console.error('AlloyProvider - redirecting to redirectUrl');
      window.location.assign(redirectUrl);
    }
  }, [redirectUrl]);
  return null;
};

const LocaleSetter = () => {
  const { user } = useUser();
  const localeFromEntitlements = user?.locale;

  const oxygenLocal = supportedLocales.find(
    (locale) => locale === localeFromEntitlements,
  );
  const { setLocale } = useLocale(noop);

  useEffect(() => {
    oxygenLocal && setLocale && setLocale(oxygenLocal);
  }, [oxygenLocal, setLocale]);

  return null;
};

type AlloyProviderProps = {
  projectId?: string;
  accountId?: string;
  productId?: ComponentProps<typeof CurrentProductIdProvider>['productId'];
  toolId?: ComponentProps<typeof CurrentToolIdProvider>['toolId'];
  autoFetchEntities?: AutoFetchType[];
  disableAllProjectsFetch?: boolean;
  locale?: string;
  children?: ReactNode;
};

/**
 * AlloyProvider to mount all project data context providers.
 */
const AlloyProvider: FC<AlloyProviderProps> = ({
  children,
  projectId,
  accountId,
  productId,
  toolId,
  autoFetchEntities = Object.values(AUTO_FETCH_TYPES),
  disableAllProjectsFetch,
  locale,
}) => (
  <AlloyLaunchDarklyProvider>
    <LocaleProvider locale={locale}>
      <Reset />
      <NotificationsProvider>
        <CurrentProductIdProvider productId={productId}>
          <CurrentToolIdProvider toolId={toolId}>
            <CurrentProjectIdProvider projectId={projectId}>
              <ProjectEntitlementsProvider
                disableAllProjectsFetch={disableAllProjectsFetch}
              >
                <CheckRedirectEntitlementsResponse />
                <CurrentAccountIdProvider accountId={accountId}>
                  <AccountEntitlementsProvider
                    disabled={
                      !autoFetchEntities.includes(
                        AUTO_FETCH_TYPES.CURRENT_ACCOUNTS_ENTITLEMENTS,
                      )
                    }
                  >
                    <UserProvider disabled>
                      <AlloyLaunchDarklySubscriber />
                      <LocaleSetter />
                      <DeletedUsersProvider>
                        <UsersProvider>
                          <CompaniesProvider>
                            <RolesProvider>
                              <SubscriptionsProvider>
                                {children}
                              </SubscriptionsProvider>
                            </RolesProvider>
                          </CompaniesProvider>
                        </UsersProvider>
                      </DeletedUsersProvider>
                    </UserProvider>
                  </AccountEntitlementsProvider>
                </CurrentAccountIdProvider>
              </ProjectEntitlementsProvider>
            </CurrentProjectIdProvider>
          </CurrentToolIdProvider>
        </CurrentProductIdProvider>
      </NotificationsProvider>
    </LocaleProvider>
  </AlloyLaunchDarklyProvider>
);

AlloyProvider.displayName = 'AlloyProvider';

AlloyProvider.propTypes = {
  /** Nodes with access to context */
  children: PropTypes.node,
  /** Project ID to fetch data for (entitlements/users/roles/companies etc.) */
  projectId: PropTypes.string,
  /** The current account ID */
  accountId: PropTypes.string,
  /** The current project ID (build/docs etc.) */
  productId: PropTypes.oneOf(Object.values(PRODUCT_IDS)),
  /** The current project ID (build/docs etc.) */
  toolId: PropTypes.oneOf(Object.values(TOOL_IDS)),
  /** Enable auto fetch for specific types */
  autoFetchEntities: PropTypes.arrayOf(
    PropTypes.oneOf(Object.values(AUTO_FETCH_TYPES)),
  ) as Validator<AutoFetchType[]>,
  /** Whether to disable all projects fetch when page loads */
  disableAllProjectsFetch: PropTypes.bool,
  /** Locale, defaults to 'en' */
  locale: PropTypes.string,
};

export default Object.assign(AlloyProvider, { AUTO_FETCH_TYPES });
